import React, { useEffect, useState } from 'react';
import { Grid, TextField, Typography, Box, InputBase, Paper, InputAdornment, Button, FormControlLabel, Checkbox, Divider, Switch } from '@mui/material';
import dayjs from 'dayjs';
import Tooltip from '@mui/material/Tooltip';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import axios from 'axios';
import { toast } from 'react-toastify';
import { isDateTimeGreaterThan30MinutesAgo } from '../../../../../utils/dateTimeUtils';

interface VitalAndIOPChartProps {
    onChange: (data: any) => void;
    data: any;
    otherTabsData: any;
    profile: any;
    handleSave: (shouldClose: boolean, searchId?: any) => void;
}

interface IOPChartEntry {
    vitals: {
        temperature: string;
        BP: string;
        RR: string;
        HR: string;
        spo: string;
    };
    iop_Input: {
        oralFeed: string;
        NG_Feed: string;
        IVFluid: string;
        TPN: string;
        calories: string;
    };
    iop_Output: {
        urine: { value: string; checked: boolean; quant: string };
        vomiting: { value: string; checked: boolean; quant: string };
        drain: { value: string; checked: boolean; quant: string };
        ngAspirate: { value: string; checked: boolean; quant: string };
        stool: { value: string; checked: boolean; quant: string };
    };
    fluid_Balance: {
        input: string;
        output: string;
        difference: string;
    };
    blood_Sugar: {
        BSR: string;
        comment: string;
    };
    dateTime?: string;
    doctorName?: string;
}

interface SaveData {
    IOP_Chart: IOPChartEntry[];
}

const normalizeVitalIOPData = (rawData: any): SaveData => {
    if (!rawData) {
        return { IOP_Chart: [] };
    }
    if (Array.isArray(rawData.IOP_Chart)) {
        return { IOP_Chart: rawData.IOP_Chart };
    }
    if (Array.isArray(rawData.formData?.IOP_Chart)) {
        return { IOP_Chart: rawData.formData.IOP_Chart };
    }
    return { IOP_Chart: [] };
};

const VitalAndIOPChart: React.FC<VitalAndIOPChartProps> = ({ onChange, data, otherTabsData, profile, handleSave }) => {
    const [formData, setFormData] = useState<any>({
        vitals: {
            temperature: '',
            BP: '',
            RR: '',
            HR: '',
            spo: '',
        },
        iop_Input: {
            oralFeed: '',
            NG_Feed: '',
            IVFluid: '',
            TPN: '',
            calories: '',
        },
        iop_Output: {
            urine: { value: '', checked: false, quant: '' },
            vomiting: { value: '', checked: false, quant: '' },
            drain: { value: '', checked: false, quant: '' },
            ngAspirate: { value: '', checked: false, quant: '' },
            stool: { value: '', checked: false, quant: '' },
        },
        fluid_Balance: {
            input: '',
            output: '',
            difference: '',
        },
        blood_Sugar: {
            BSR: '',
            comment: '',
        },
    });
    const [showInput, setShowInput] = useState<boolean>(false);
    const [showOutput, setShowOutput] = useState<boolean>(false);
    const [showFluid, setShowFluid] = useState<boolean>(false);
    const [showVitals, setShowVitals] = useState<boolean>(true);
    const [isComponentVisible, setIsComponentVisible] = useState(false);
    const [saveData, setSaveData] = useState<SaveData>({
        IOP_Chart: [],
    });
    const [isEditMode, setIsEditMode] = useState(false);
    const [editIndex, setEditIndex] = useState<number | null>(null);
    const [searchId, setSearchId] = useState<any>();

    const fetchVitalIopChart = async (admissionId: string) => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_IPD_API_URL}/vital-iop-chart/getByAdmissionId/${admissionId}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                    },
                }
            );
            const chartData = normalizeVitalIOPData(response?.data?.data);
            setSaveData(chartData);
            onChange(chartData);
        } catch (error) {
            console.error('Error fetching vital IOP chart:', error);
        }
    };

    useEffect(() => {
        if (otherTabsData?._id) {
            setSearchId(otherTabsData._id);
            fetchVitalIopChart(otherTabsData._id);
        }
    }, [otherTabsData?._id]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        const [section, field, subfield] = name.split('.');
        let updatedFormData = { ...formData };

        if (section === 'iop_Output') {
            if (type === 'checkbox') {
                updatedFormData = {
                    ...updatedFormData,
                    [section]: {
                        ...updatedFormData[section],
                        [field]: {
                            ...updatedFormData[section][field],
                            checked: checked,
                        },
                    },
                };
            } else if (subfield === 'quant') {
                updatedFormData = {
                    ...updatedFormData,
                    [section]: {
                        ...updatedFormData[section],
                        [field]: {
                            ...updatedFormData[section][field],
                            quant: value,
                        },
                    },
                };
            } else {
                updatedFormData = {
                    ...updatedFormData,
                    [section]: {
                        ...updatedFormData[section],
                        [field]: {
                            ...updatedFormData[section][field],
                            value: value,
                        },
                    },
                };
            }
        } else if (section === 'iop_Input') {
            updatedFormData = {
                ...updatedFormData,
                [section]: {
                    ...updatedFormData[section],
                    [field]: value,
                },
            };
        } else {
            updatedFormData = {
                ...updatedFormData,
                [section]: {
                    ...updatedFormData[section],
                    [field]: value,
                },
            };
        }

        const fluidInput: any = (
            parseFloat(updatedFormData.iop_Input.oralFeed || 0) +
            parseFloat(updatedFormData.iop_Input.NG_Feed || 0) +
            parseFloat(updatedFormData.iop_Input.IVFluid || 0) +
            parseFloat(updatedFormData.iop_Input.TPN || 0) +
            parseFloat(updatedFormData.iop_Input.calories || 0)
        ).toFixed(2);

        const fluidOutput: any = (
            parseFloat(updatedFormData.iop_Output.urine.quant || 0) +
            parseFloat(updatedFormData.iop_Output.vomiting.quant || 0) +
            parseFloat(updatedFormData.iop_Output.drain.quant || 0) +
            parseFloat(updatedFormData.iop_Output.ngAspirate.quant || 0) +
            parseFloat(updatedFormData.iop_Output.stool.quant || 0)
        ).toFixed(2);

        updatedFormData.fluid_Balance.input = fluidInput;
        updatedFormData.fluid_Balance.output = fluidOutput;
        const totalDiff: any = fluidInput - fluidOutput;

        if (fluidInput !== 0 && fluidOutput !== 0) {
            updatedFormData.fluid_Balance.difference = Math.abs(totalDiff);
        } else {
            updatedFormData.fluid_Balance.difference = 0;
        }

        setFormData(updatedFormData);
    };

    const handleEdit = (index: number) => {
        setIsComponentVisible(true);
        setShowFluid(true)
        setShowOutput(true)
        setShowInput(true)
        const dataToEdit: any = saveData?.IOP_Chart[index];
        setFormData({
            vitals: dataToEdit.vitals,
            iop_Input: dataToEdit.iop_Input,
            iop_Output: dataToEdit.iop_Output,
            fluid_Balance: dataToEdit?.fluid_Balance,
            blood_Sugar: dataToEdit?.blood_Sugar,
        });

        setIsEditMode(true);
        setEditIndex(index);
    };

    const handleSaveIOP = async () => {
        const existingChart = Array.isArray(saveData?.IOP_Chart) ? saveData.IOP_Chart : [];
        const existingEntry =
            isEditMode && editIndex !== null ? existingChart[editIndex] : null;

        const payload = {
            admissionId: otherTabsData?._id || searchId,
            vitals: formData.vitals,
            iop_Input: formData.iop_Input,
            iop_Output: formData.iop_Output,
            fluid_Balance: formData.fluid_Balance,
            blood_Sugar: formData.blood_Sugar,
            dateTime: existingEntry?.dateTime ?? dayjs().format('DD.MM.YYYY hh:mm A'),
            doctorName: existingEntry?.doctorName ?? profile?.data?.fullName,
        };

        try {
            let response;
            if (isEditMode && editIndex !== null) {
                response = await axios.put(
                    `${process.env.REACT_APP_IPD_API_URL}/vital-iop-chart/updateByIndex/${editIndex}`,
                    payload,
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem('token')}`,
                            'Content-Type': 'application/json',
                        },
                    }
                );
                if (response?.data?.isSuccess) {
                    toast.success('Vital & IOP entry updated successfully');
                }
            } else {
                response = await axios.post(
                    `${process.env.REACT_APP_IPD_API_URL}/vital-iop-chart/create-entry`,
                    payload,
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem('token')}`,
                            'Content-Type': 'application/json',
                        },
                    }
                );
                if (response?.data?.isSuccess) {
                    toast.success('Vital & IOP entry saved successfully');
                }
            }

            await fetchVitalIopChart(otherTabsData?._id || searchId);

            setFormData({
                vitals: { temperature: '', BP: '', RR: '', HR: '', spo: '' },
                iop_Input: { oralFeed: '', NG_Feed: '', IVFluid: '', TPN: '', calories: '' },
                iop_Output: { urine: { value: '', checked: false, quant: '' }, vomiting: { value: '', checked: false, quant: '' }, drain: { value: '', checked: false, quant: '' }, ngAspirate: { value: '', checked: false, quant: '' }, stool: { value: '', checked: false, quant: '' } },
                fluid_Balance: { input: '', output: '', difference: '' },
                blood_Sugar: { BSR: '', comment: '' },
            });
            setIsEditMode(false);
            setEditIndex(null);
            setIsComponentVisible(false);
            setShowOutput(false);
            setShowFluid(false);
            setShowInput(false);
        } catch (error) {
            console.error('Error saving vital IOP chart:', error);
            toast.error('Failed to save vital & IOP data');
        }
    };

    useEffect(() => {
        if (data) {
            setSaveData(normalizeVitalIOPData(data));
        }
    }, [data]);

    return (
        <div id="top">
            <Box
                sx={{
                    bgcolor: "rgba(233, 233, 237, 1)",
                    padding: 0.25,
                    textAlign: "center",
                    mt: 0.5,
                    mb: 0.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    height: '30px',
                }}
            >
                <Typography sx={{ fontSize: "14px", fontWeight: "600" }}>
                    Vitals and IOP
                </Typography>
                <FormControlLabel
                    control={
                        <Switch
                            checked={isComponentVisible}
                            onChange={() => setIsComponentVisible(!isComponentVisible)}
                            color="primary"
                        />
                    }
                    label="+Add Vital"
                />
            </Box>
            {
                isComponentVisible && <>
                    <Grid container spacing={1} sx={{ mt: 1.2 }}>
                        <Grid item md={1} sm={1} xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size='small'
                                        checked={showVitals}
                                        onChange={() => setShowVitals(!showVitals)}
                                        name="showInput"
                                        sx={{
                                            padding: '4px'
                                        }}
                                    />
                                }
                                label={<span style={{ fontSize: '12px', fontWeight: 'bold' }}>Vitals</span>}
                            />
                        </Grid>
                        {
                            showVitals && <>
                                <Grid item md={2.2} sm={3} xs={4}>
                                    <TextField
                                        label="Temp"
                                        placeholder="92-107"
                                        size="small"
                                        className='search-field' 
                                        value={formData?.vitals?.temperature}
                                        name="vitals.temperature"
                                        onChange={(e: any) => {
                                            const value = e.target.value;
                                            if (!isNaN(value) && value <= 107) {
                                                handleChange(e);
                                            }
                                        }}
                                        fullWidth
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position='end'>
                                                    <span style={{ fontSize: '12px' }}>F</span>
                                                </InputAdornment>
                                            )
                                        }}
                                        error={
                                            !!formData.vitals.temperature &&
                                            (parseFloat(formData.vitals.temperature) < 92 || parseFloat(formData.vitals.temperature) > 107)
                                        }
                                        helperText={null}
                                    />
                                </Grid>
                                <Grid item md={2.2} sm={3} xs={4}>
                                    <TextField
                                        label="BP"
                                        placeholder="120/80"
                                        size="small"
                                        className='search-field'
                                        value={formData?.vitals?.BP}
                                        name="vitals.BP"
                                        onChange={(e: any) => {
                                            const value = e.target.value;
                                            if (value === "") {
                                                handleChange(e);
                                                return;
                                            }
                                            const regex = /^(\d{1,3})\/?(\d{0,3})$/;
                                            if (regex.test(value)) {
                                                const parts = value.split('/');
                                                const firstPart = parseInt(parts[0], 10);
                                                const secondPart = parts[1] ? parseInt(parts[1], 10) : 0;
                                                if (firstPart >= 1 && firstPart <= 300 && secondPart <= firstPart) {
                                                    handleChange(e);
                                                }
                                            }
                                        }}
                                        fullWidth
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position='end'>
                                                    <span style={{ fontSize: '12px' }}>mgHg</span>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Grid>
                                <Grid item md={2.2} sm={3} xs={4}>
                                    <TextField
                                        label="RR" // Label for the input
                                        placeholder="1-80"
                                        size="small"
                                        value={formData?.vitals?.RR}
                                        name="vitals.RR"
                                        className='search-field'
                                        onChange={(e: any) => {
                                            const value = e.target.value;
                                            if (/^\d*$/.test(value) && (value === "" || (parseInt(value) >= 1 && parseInt(value) <= 80))) {
                                                handleChange(e);
                                            }
                                        }}
                                        fullWidth
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position='end'>
                                                    <span style={{ fontSize: '12px' }}>MIn</span>
                                                </InputAdornment>
                                            )
                                        }}
                                        error={!!formData.vitals.RR && (parseInt(formData.vitals.RR) < 1 || parseInt(formData.vitals.RR) > 80)}
                                        helperText={
                                            formData.vitals.rr &&
                                                (parseInt(formData.vitals.RR) < 1 || parseInt(formData.vitals.RR) >= 80)
                                                ? 'Please enter a value between 1 and 80'
                                                : ''
                                        }
                                    />
                                </Grid>
                                <Grid item md={2.2} sm={3} xs={4}>
                                    <TextField
                                        label="HR"
                                        placeholder="50-250"
                                        size="small"
                                        className='search-field'
                                        value={formData?.vitals?.HR}
                                        name="vitals.HR"
                                        onChange={(e: any) => {
                                            const value = e.target.value;
                                            if (value <= 250) {
                                                handleChange(e);
                                            }
                                        }}
                                        error={!!formData.vitals.HR && (parseInt(formData.vitals.HR) < 50 || parseInt(formData.vitals.HR) > 250)}
                                        fullWidth
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position='end'>
                                                    <span style={{ fontSize: '12px' }}>MIn</span>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Grid>
                                <Grid item md={2.2} sm={3} xs={4}>
                                    <TextField
                                        label="SPO₂"
                                        placeholder="0-100"
                                        size="small"
                                        name='vitals.spo'
                                        value={formData?.vitals?.spo}
                                        onChange={(e: any) => {
                                            const value = e.target.value;
                                            if (value <= 100) {
                                                handleChange(e);
                                            }
                                        }}
                                        fullWidth
                                        className='search-field'
                                        type='number'
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position='end'>
                                                    <span style={{ fontSize: '12px' }}>%</span>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Grid>
                            </>
                        }
                    </Grid>
                    <Box mt={1.5}>
                        <Grid container spacing={1}>
                            <Grid item md={1} sm={1} xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox size='small'
                                            checked={showInput}
                                            onChange={() => setShowInput(!showInput)}
                                            name="showInput"
                                            sx={{
                                                padding: '4px'
                                            }}
                                        />
                                    }
                                    label={<span style={{ fontSize: '12px', fontWeight: 'bold' }}>Input</span>}
                                />
                            </Grid>
                            {
                                showInput && <>
                                    <Grid item md={2.2} sm={3} xs={4}>
                                        <TextField
                                            label="NG Feed"
                                            variant="outlined"
                                            fullWidth
                                            name="iop_Input.NG_Feed"
                                            value={formData?.iop_Input?.NG_Feed}
                                            size='small'
                                            className='search-field'
                                            // onChange={handleChange}
                                            onChange={(e: any) => {
                                                const value = e.target.value;
                                                if (/^\d{0,3}(\.\d{0,1})?$/.test(value)) {
                                                    handleChange(e);
                                                }
                                            }}
                                            type='number'
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <span>ml</span>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                    <Grid item md={2.2} sm={3} xs={4}>
                                        <TextField
                                            label="Oral Feed"
                                            variant="outlined"
                                            fullWidth
                                            name="iop_Input.oralFeed"
                                            value={formData?.iop_Input?.oralFeed}
                                            className='search-field'
                                            size='small'
                                            // onChange={handleChange}
                                            onChange={(e: any) => {
                                                const value = e.target.value;
                                                if (/^\d{0,4}(\.\d{0,1})?$/.test(value)) {
                                                    handleChange(e);
                                                }
                                            }}
                                            type='number'
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <span style={{ fontSize: '12px' }}>ml</span>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                    <Grid item md={2.2} sm={3} xs={4}>
                                        <TextField
                                            label="IV Fluid"
                                            variant="outlined"
                                            fullWidth
                                            name="iop_Input.IVFluid"
                                            value={formData?.iop_Input?.IVFluid}
                                            className='search-field'
                                            size='small'
                                            // onChange={handleChange}
                                            onChange={(e: any) => {
                                                const value = e.target.value;
                                                if (/^\d{0,4}(\.\d{0,1})?$/.test(value)) {
                                                    handleChange(e);
                                                }
                                            }}
                                            type='number'
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <span>ml</span>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>
                                    <Grid item md={2.2} sm={3} xs={4}>
                                        <TextField
                                            label="TPN"
                                            variant="outlined"
                                            fullWidth
                                            name="iop_Input.TPN"
                                            value={formData?.iop_Input?.TPN}
                                            className='search-field'
                                            size='small'
                                            // onChange={handleChange}
                                            onChange={(e: any) => {
                                                const value = e.target.value;
                                                if (/^\d{0,4}(\.\d{0,1})?$/.test(value)) {
                                                    handleChange(e);
                                                }
                                            }}
                                            type='number'
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <span>ml</span>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>
                                    <Grid item md={2.2} sm={3} xs={4}>
                                        <TextField
                                            label="Calories"
                                            variant="outlined"
                                            fullWidth
                                            name="iop_Input.calories"
                                            value={formData?.iop_Input?.calories}
                                            className='search-field'
                                            size='small'
                                            // onChange={handleChange}
                                            onChange={(e: any) => {
                                                const value = e.target.value;
                                                if (/^\d{0,4}(\.\d{0,1})?$/.test(value)) {
                                                    handleChange(e);
                                                }
                                            }}
                                            type='number'
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <span>kcal</span>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>
                                </>
                            }
                        </Grid>
                    </Box>
                    <Box mt={1} >
                        <Grid container spacing={2}>
                            <Grid item md={1} sm={1} xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox size='small'
                                            checked={showOutput}
                                            onChange={() => setShowOutput(!showOutput)} // Toggle the checkbox state
                                            name="showOutput"
                                            sx={{
                                                padding: '4px'
                                            }}
                                        />
                                    }
                                    label={<span style={{ fontSize: '12px', fontWeight: 'bold' }}>Output</span>}
                                />
                            </Grid>

                            {
                                showOutput && <>
                                    <Grid item md={2.2} sm={3} xs={4}>
                                        <Box display='flex' alignItems='center' gap='2px'>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={formData?.iop_Output?.urine?.checked}
                                                        onChange={handleChange}
                                                        name="iop_Output.urine.checked"
                                                        color="primary"
                                                        size="small"
                                                        sx={{
                                                            width: '16px',
                                                            height: '16px',
                                                            padding: 0,
                                                        }}
                                                    />
                                                }
                                                label={<span style={{ fontSize: '12px' }}>Urine</span>}
                                            />
                                            {formData?.iop_Output?.urine.checked && <TextField
                                                label="Quantity"
                                                variant="standard"
                                                fullWidth
                                                name="iop_Output.urine.quant"
                                                value={formData?.iop_Output?.urine?.quant}
                                                // className="search-field"
                                                size="small"
                                                type='number'
                                                // onChange={handleChange}
                                                onChange={(e: any) => {
                                                    const value = e.target.value;
                                                    if (/^\d{0,4}(\.\d{0,1})?$/.test(value)) {
                                                        handleChange(e);
                                                    }
                                                }}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <span>ml</span>
                                                        </InputAdornment>

                                                    ),
                                                    style: {
                                                        fontSize: '12px',
                                                        width: '80%'
                                                    }
                                                }}
                                                disabled={!formData?.iop_Output?.urine?.checked}
                                            />}
                                        </Box>
                                        {formData?.iop_Output?.urine.checked && <TextField
                                            label="Details"
                                            fullWidth
                                            name="iop_Output.urine.value"
                                            value={formData?.iop_Output?.urine?.value}
                                            size="small"
                                            sx={{ mt: .6 }}
                                            onChange={handleChange}
                                            className='search-field'
                                            disabled={!formData?.iop_Output?.urine?.checked}
                                        />}
                                    </Grid>

                                    <Grid item md={2.2} sm={3} xs={4}>
                                        <Box display='flex' alignItems='center' gap='2px'>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        sx={{
                                                            width: '16px',
                                                            height: '16px',
                                                            padding: 0,
                                                        }}
                                                        checked={formData?.iop_Output?.vomiting?.checked}
                                                        onChange={handleChange}
                                                        name="iop_Output.vomiting.checked"
                                                        size="small"
                                                        color="primary"
                                                    />
                                                }
                                                label={<span style={{ fontSize: '12px' }}>Vomiting</span>}
                                            />
                                            {formData?.iop_Output?.vomiting?.checked && <TextField
                                                label="Quantity"
                                                variant="standard"
                                                fullWidth
                                                name="iop_Output.vomiting.quant"
                                                value={formData?.iop_Output?.vomiting?.quant}
                                                size="small"
                                                type='number'
                                                // onChange={handleChange}
                                                onChange={(e: any) => {
                                                    const value = e.target.value;
                                                    if (/^\d{0,4}(\.\d{0,1})?$/.test(value)) {
                                                        handleChange(e);
                                                    }
                                                }}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <span>ml</span>
                                                        </InputAdornment>
                                                    ),
                                                    style: {
                                                        fontSize: '12px',
                                                        width: '80%'
                                                    }
                                                }}
                                                disabled={!formData?.iop_Output?.vomiting?.checked}
                                            />}
                                        </Box>
                                        {formData?.iop_Output?.vomiting?.checked && <TextField
                                            label="Details"
                                            fullWidth
                                            name="iop_Output.vomiting.value"
                                            value={formData?.iop_Output?.vomiting?.value}
                                            size="small"
                                            sx={{ mt: .6 }}
                                            onChange={handleChange}
                                            className='search-field'
                                            disabled={!formData?.iop_Output?.vomiting?.checked}
                                        />}
                                    </Grid>

                                    <Grid item md={2.2} sm={3} xs={4}>
                                        <Box display='flex' alignItems='center' gap='2px'>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        sx={{
                                                            width: '16px',
                                                            height: '16px',
                                                            padding: 0,
                                                        }}
                                                        checked={formData?.iop_Output?.drain?.checked}
                                                        onChange={handleChange}
                                                        name="iop_Output.drain.checked"
                                                        size="small"
                                                        color="primary"
                                                    />
                                                }
                                                label={<span style={{ fontSize: '12px' }}>Drain</span>}
                                            />
                                            {formData?.iop_Output?.drain?.checked &&
                                                <TextField
                                                    label="Quantity"
                                                    variant="standard"
                                                    fullWidth
                                                    name="iop_Output.drain.quant"
                                                    value={formData?.iop_Output?.drain?.quant}
                                                    size="small"
                                                    type='number'
                                                    // onChange={handleChange}
                                                    onChange={(e: any) => {
                                                        const value = e.target.value;
                                                        if (/^\d{0,4}(\.\d{0,1})?$/.test(value)) {
                                                            handleChange(e);
                                                        }
                                                    }}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <span>ml</span>
                                                            </InputAdornment>
                                                        ),
                                                        style: {
                                                            fontSize: '12px',
                                                            width: '80%'
                                                        }
                                                    }}
                                                    disabled={!formData?.iop_Output?.drain?.checked}
                                                />}
                                        </Box>
                                        {
                                            formData?.iop_Output?.drain?.checked && < TextField
                                                label="Details"
                                                fullWidth
                                                name="iop_Output.drain.value"
                                                value={formData?.iop_Output?.drain?.value}
                                                size="small"
                                                className='search-field'
                                                onChange={handleChange}
                                                sx={{ mt: .6 }}
                                                disabled={!formData?.iop_Output?.drain?.checked}
                                            />
                                        }
                                    </Grid>

                                    <Grid item md={2.2} sm={3} xs={4}>
                                        <Box display='flex' alignItems='center' gap='2px'>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={formData?.iop_Output?.ngAspirate?.checked}
                                                        onChange={handleChange}
                                                        name="iop_Output.ngAspirate.checked"
                                                        size="small"
                                                        color="primary"
                                                        sx={{
                                                            width: '16px',
                                                            height: '16px',
                                                            padding: 0,
                                                        }}
                                                    />
                                                }
                                                label={<span style={{ fontSize: '10px' }}>NG_Aspirate</span>}
                                            />
                                            {formData?.iop_Output?.ngAspirate?.checked && <TextField
                                                label="Quantity"
                                                variant="standard"
                                                fullWidth
                                                name="iop_Output.ngAspirate.quant"
                                                value={formData?.iop_Output?.ngAspirate?.quant}
                                                size="small"
                                                type='number'
                                                // onChange={handleChange}
                                                onChange={(e: any) => {
                                                    const value = e.target.value;
                                                    if (/^\d{0,4}(\.\d{0,1})?$/.test(value)) {
                                                        handleChange(e);
                                                    }
                                                }}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <span>ml</span>
                                                        </InputAdornment>
                                                    ),
                                                    style: {
                                                        fontSize: '12px',
                                                        width: '80%'
                                                    }
                                                }}
                                                disabled={!formData?.iop_Output?.ngAspirate?.checked}
                                            />}
                                        </Box>
                                        {formData?.iop_Output?.ngAspirate?.checked && <Box>
                                            <TextField
                                                label="Details"
                                                fullWidth
                                                name="iop_Output.ngAspirate.value"
                                                value={formData?.iop_Output?.ngAspirate?.value}
                                                size="small"
                                                sx={{ mt: .6 }}
                                                onChange={handleChange}
                                                className='search-field'
                                                disabled={!formData?.iop_Output?.ngAspirate?.checked}
                                            />
                                        </Box>}
                                    </Grid>

                                    <Grid item md={2.2} sm={3} xs={4}>
                                        <Box display='flex' alignItems='center' gap='2px'>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={formData?.iop_Output?.stool?.checked}
                                                        onChange={handleChange}
                                                        name="iop_Output.stool.checked"
                                                        color="primary"
                                                        size="small"
                                                        sx={{
                                                            width: '16px',
                                                            height: '16px',
                                                            padding: 0,
                                                        }}
                                                    />
                                                }
                                                label={<span style={{ fontSize: '12px' }}>Stool</span>}
                                            />
                                            {formData?.iop_Output?.stool?.checked && <TextField
                                                label="Quantity"
                                                variant="standard"
                                                fullWidth
                                                name="iop_Output.stool.quant"
                                                value={formData?.iop_Output?.stool?.quant}
                                                size="small"
                                                type='number'
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <span>ml</span>
                                                        </InputAdornment>
                                                    ),
                                                    style: {
                                                        fontSize: '12px',
                                                        width: '80%'
                                                    }
                                                }}
                                                onChange={handleChange}
                                                disabled={!formData?.iop_Output?.stool?.checked}
                                            />}
                                        </Box>
                                        {
                                            formData?.iop_Output?.stool?.checked && <TextField
                                                label="Details"
                                                fullWidth
                                                name="iop_Output.stool.value"
                                                value={formData?.iop_Output?.stool?.value}
                                                size="small"
                                                sx={{ mt: .6 }}
                                                className='search-field'
                                                onChange={handleChange}
                                                disabled={!formData?.iop_Output?.stool?.checked}
                                            />
                                        }
                                    </Grid>
                                </>
                            }
                        </Grid>
                    </Box>
                    <Box mt={1.5} >
                        <Grid container spacing={2}>
                            <Grid item md={1} sm={1} xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox size='small'
                                            checked={showFluid}
                                            onChange={() => setShowFluid(!showFluid)}
                                            name="showFluid"
                                            sx={{
                                                padding: '4px'
                                            }}
                                        />
                                    }
                                    label={<span style={{ fontSize: '12px', fontWeight: 'bold' }}>Fluid Balance</span>}
                                />
                            </Grid>
                            {
                                showFluid && <>
                                    <Grid item md={2.2} sm={3} xs={4}>
                                        <TextField
                                            label="Input"
                                            variant="outlined"
                                            fullWidth
                                            name="fluid_Balance.input"
                                            value={formData?.fluid_Balance?.input}
                                            className='search-field'
                                            onChange={handleChange}
                                            size='small'
                                            type='number'
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position='end'>
                                                        <span style={{ fontSize: '12px' }}>ml</span>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>
                                    <Grid item md={2.2} sm={3} xs={4}>
                                        <TextField
                                            label="Output"
                                            variant="outlined"
                                            fullWidth
                                            size='small'
                                            name="fluid_Balance.output"
                                            value={formData?.fluid_Balance?.output}
                                            className='search-field'
                                            onChange={handleChange}
                                            type='number'
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position='end'>
                                                        <span style={{ fontSize: '12px' }}>ml</span>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>
                                    <Grid item md={2.2} sm={3} xs={4}>
                                        <TextField
                                            label="Difference"
                                            variant="outlined"
                                            fullWidth
                                            name="fluid_Balance.difference"
                                            value={formData?.fluid_Balance?.difference}
                                            onChange={handleChange}
                                            size='small'
                                            className='search-field'
                                            type='number'
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position='end'>
                                                        <span style={{ fontSize: '12px' }}>ml</span>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>
                                    <Grid item md={2.2} sm={3} xs={4}>
                                        <TextField
                                            label="BSR"
                                            variant="outlined"
                                            fullWidth
                                            name="blood_Sugar.BSR"
                                            type='number'
                                            value={formData?.blood_Sugar?.BSR || ''}
                                            size="small"
                                            onChange={(e: any) => {
                                                const value = e.target.value;
                                                if (/^\d{0,3}$/.test(value)) {
                                                    handleChange(e);
                                                }
                                            }}
                                            className="search-field"
                                            inputProps={{
                                                maxLength: 3,
                                            }}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position='end'>
                                                        <span style={{ fontSize: '10px' }}>{
                                                            formData?.blood_Sugar?.BSR
                                                                ? Number(formData.blood_Sugar.BSR) < 30
                                                                    ? 'Low'
                                                                    : Number(formData.blood_Sugar.BSR) > 600
                                                                        ? 'High'
                                                                        : ''
                                                                : ''
                                                        }</span>
                                                    </InputAdornment>

                                                )
                                            }}
                                            error={
                                                (formData?.blood_Sugar?.BSR && (Number(formData.blood_Sugar.BSR) < 30 || Number(formData.blood_Sugar.BSR) > 600))
                                            }
                                        />
                                    </Grid>
                                    <Grid item md={2.2} sm={3} xs={4}>
                                        <TextField
                                            label="Comment"
                                            variant="outlined"
                                            fullWidth
                                            name="blood_Sugar.comment"
                                            value={formData?.blood_Sugar?.comment}
                                            size='small'
                                            onChange={handleChange}
                                            className='search-field'
                                        />
                                    </Grid>
                                </>
                            }
                        </Grid>
                    </Box>
                    <Box display='flex' justifyContent='flex-end' mt={2} mr={2.2}>
                        <Button onClick={handleSaveIOP} variant='contained' size='small' disabled={!showInput && !showOutput && !showFluid && !showVitals}>
                            {isEditMode ? 'Update' : 'Save'}
                        </Button>
                    </Box>
                </>
            }
            <Box mt={2}>
                <Typography sx={{ fontSize: '12px', fontWeight: '600', marginBottom: '8px' }}>Vital & IOP History</Typography>
                <Grid container spacing={2}>
                    {Array.isArray(saveData?.IOP_Chart) && [...saveData.IOP_Chart]
                        .map((item: any, index: number) => ({ item, index }))
                        .reverse()
                        .map(({ item, index: originalIndex }) => {
                        const { vitals, iop_Input, iop_Output, fluid_Balance, blood_Sugar } = item;
                        const doctorName = item?.doctorName || '';
                        const dateTime = item?.dateTime || 'DD.MM.YYYY hh:mm:ss A';
                        return (
                            <Grid item md={6} sm={12} xs={12} key={`${dateTime}-${originalIndex}`}>
                                <Box sx={{ backgroundColor: '#f9f9f9', padding: 1, borderRadius: 2, boxShadow: 2 }}>
                                    <Box >
                                        <Grid container spacing={1}>
                                            {[
                                                { label: 'BP', value: vitals?.BP || '', unit: '' },
                                                { label: 'Temp', value: vitals?.temperature || '', unit: 'F' },
                                                { label: 'RR', value: vitals?.RR || '', unit: '/Min' },
                                                { label: 'HR', value: vitals?.HR || '', unit: '/Min' },
                                                { label: 'SPO₂', value: vitals?.spo || '', unit: '%' },
                                            ]?.map((item, i) => (
                                                <Grid item xs={2.4} key={i}>
                                                    <Typography sx={{ fontSize: '10px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                                                        <h3 style={{ fontWeight: 'bold' }}>{item?.label}</h3> : {item?.value} {item?.unit}
                                                    </Typography>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                    <Divider sx={{ width: '100%', my: 0.5, borderColor: '#e0e0e0' }} />
                                    <Box >
                                        <Grid container spacing={1}>
                                            <Grid item md={1.5}>
                                                <Typography sx={{ fontSize: '10px', fontWeight: 'bold', color: 'blue' }}>Input</Typography>
                                            </Grid>
                                            {[
                                                { label: 'Oral Feed', value: iop_Input?.oralFeed || '00 ml' },
                                                { label: 'Oral Fluid', value: iop_Input?.NG_Feed || '00 ml' },
                                                { label: 'IV Fluid', value: iop_Input?.IVFluid || '00 ml' },
                                                { label: 'TPN', value: iop_Input?.TPN || '00 ml' },
                                                { label: 'Calories', value: iop_Input?.calories || '00 kcal' }
                                            ]?.map((item, i) => (
                                                <Grid item xs={2.1} key={i}>
                                                    <Typography sx={{ fontSize: '10px', display: 'flex', alignItems: 'center' }}>
                                                        <h3 style={{ fontWeight: 'bold' }}>{item?.label}</h3> : {item?.value}
                                                    </Typography>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                    <Divider sx={{ width: '100%', my: 0.5, borderColor: '#e0e0e0' }} />
                                    <Box >
                                        <Grid container spacing={1}>
                                            <Grid item md={1.5}>
                                                <Typography sx={{ fontSize: '10px', fontWeight: 'bold', color: 'blue' }}>Output</Typography>
                                            </Grid>
                                            {[
                                                { label: `Urine  ${iop_Output?.urine?.quant ? `(${iop_Output?.urine?.quant})` : ''}`, value: iop_Output?.urine?.checked ? iop_Output?.urine?.value : '' },
                                                { label: `Vomiting ${iop_Output?.vomiting?.quant ? `(${iop_Output?.vomiting?.quant})` : ''}`, value: iop_Output?.vomiting?.checked ? iop_Output?.vomiting?.value : '' },
                                                { label: `Drain ${iop_Output?.drain?.quant ? `(${iop_Output?.drain?.quant})` : ''}`, value: iop_Output?.drain?.checked ? iop_Output?.drain?.value : '' },
                                                { label: `NG Aspirate ${iop_Output?.ngAspirate?.quant ? `(${iop_Output?.ngAspirate?.quant})` : ''}`, value: iop_Output?.ngAspirate?.checked ? iop_Output?.ngAspirate?.value : '' },
                                                { label: `Stool ${iop_Output?.stool?.quant ? `(${iop_Output?.stool?.quant})` : ''}`, value: iop_Output?.stool?.checked ? iop_Output?.stool?.value : '' }
                                            ]?.map((item, i) => (
                                                <Grid item xs={2.1} key={i}>
                                                    <Box display="flex" flexDirection="column">
                                                        <Typography sx={{ fontSize: '10px', display: 'flex', fontWeight: 'bold' }}>
                                                            {item?.label}:
                                                        </Typography>
                                                        <Typography sx={{ fontSize: '10px' }}>
                                                            <Tooltip title={item?.value} placement='top' arrow>
                                                                <span>
                                                                    {item?.value?.length > 7 ? `${item?.value?.slice(0, 7)}...` : item?.value}
                                                                </span>
                                                            </Tooltip>
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                    <Divider sx={{ width: '100%', my: 0.5, borderColor: '#e0e0e0' }} />
                                    <Box >
                                        <Grid container spacing={1} >
                                            <Grid item md={1.5}>
                                                <Typography sx={{ fontSize: '10px', fontWeight: 'bold', color: 'blue' }}>Fluid Balance</Typography>
                                            </Grid>
                                            {[
                                                { label: 'InPut', value: fluid_Balance?.input || '00 ml' },
                                                { label: 'OutPut', value: fluid_Balance?.output || '00 ml' },
                                                { label: 'Difference', value: fluid_Balance?.difference || '00 ml' },
                                                { label: 'BSR', value: blood_Sugar?.BSR || '00' },
                                                { label: 'Comment', value: blood_Sugar?.comment || '...' }
                                            ]?.map((item, i) => (
                                                <Grid item xs={2.1} key={i}>
                                                    <Box display="flex" flexDirection="column">
                                                        <Typography sx={{ fontSize: '10px', fontWeight: 'bold', display: 'flex' }}>
                                                            {item?.label}:
                                                        </Typography>
                                                        <Typography sx={{ fontSize: '10px' }}>
                                                            <Tooltip title={item?.value} placement='top' arrow>
                                                                <span>
                                                                    {item?.value?.length > 7 ? `${item?.value.slice(0, 7)}...` : item?.value}
                                                                </span>
                                                            </Tooltip>
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                    <Divider sx={{ width: '100%', my: 0.5, borderColor: '#e0e0e0' }} />
                                    <Box display="flex" justifyContent="space-between" alignItems="center" >
                                        <Typography sx={{ fontSize: '10px', fontWeight: 'bold', color: 'blue' }}>Entered By :</Typography>
                                        <Typography sx={{ fontSize: '10px' }}>{doctorName}</Typography>
                                        <Typography sx={{ fontSize: '10px' }}>{dateTime}</Typography>
                                        {doctorName === profile?.data?.fullName && (
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                sx={{ textTransform: 'none' }}
                                                // onClick={() => handleEdit(idx)}
                                                onClick={() => {
                                                    handleEdit(originalIndex);
                                                    const topElement = document.getElementById('top');
                                                    if (topElement) {
                                                        topElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                    }
                                                }}
                                                disabled={!isDateTimeGreaterThan30MinutesAgo(dateTime)}
                                            >
                                                <EditOutlinedIcon sx={{ fontSize: '14px' }} /> Edit
                                            </Button>
                                        )}
                                    </Box>
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>
            </Box>
        </div>
    );
};

export default VitalAndIOPChart;
